import React from "react";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Box, Typography } from "@mui/material";
import { useSettings } from "../../../providers/settings-provider";
import TreeDropdown, { TreeDropdownOption } from "../../tree-dropdown";
import Player from 'lottie-react';
import okAnimation from '../../../files/json/approved.json';

export interface MatchRow {
    id: string;
    date: string;
    amount: string;
    description: string;
    suggestedCategory: string;
    unmatched: boolean;
    checked: boolean;
    // Original CSV row (every column, unmapped) - carried through to the
    // import payload so it can be stored per-transaction.
    sourceData?: unknown;
}

export interface MatchTableProps {
    rows: MatchRow[];
    onCheck?: (id: string, checked: boolean) => void;
    onCheckAll?: (checked: boolean) => void;
    allChecked?: boolean;
    canEditCategories: boolean;
    categories?: TreeDropdownOption[];
    placeholderText?: string;
    onChangeCategory?: (id: string, value: string) => void;
    startIndex?: number; // For calculating alternating row colors across pages
}

const MatchTable: React.FC<MatchTableProps> = ({ rows, onCheck, onCheckAll, allChecked, canEditCategories, categories, onChangeCategory, placeholderText, startIndex = 0 }) => {
    const { formatAmount, formatDate } = useSettings();

    const parseDate = (dateStr: string | any): Date | null => {
        if (!dateStr) return null;
        
        const s = String(dateStr).trim();
        if (!s || s === '-') return null;

        // Try native parsing first
        let d = new Date(s);
        if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
            return d;
        }

        d = new Date(s.replace(/-/g, '/'));
        if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) {
            return d;
        }

        const currentYear = new Date().getFullYear();
        
        const monthMap: { [key: string]: number } = {
            'jan': 0, 'january': 0,
            'feb': 1, 'february': 1,
            'mar': 2, 'march': 2,
            'apr': 3, 'april': 3,
            'may': 4,
            'jun': 5, 'june': 5,
            'jul': 6, 'july': 6,
            'aug': 7, 'august': 7,
            'sep': 8, 'september': 8,
            'oct': 9, 'october': 9,
            'nov': 10, 'november': 10,
            'dec': 11, 'december': 11
        };

        const patterns = [
            { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) },
            { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = parseInt(m[2], 10) - 1;
                const year = parseInt(m[3], 10);
                const testDate = new Date(year, month, day);
                if (testDate.getDate() === day && testDate.getMonth() === month) {
                    return testDate;
                }
                return new Date(year, day - 1, month + 1);
            }},
            { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = parseInt(m[2], 10) - 1;
                let year = parseInt(m[3], 10);
                year = year < 50 ? 2000 + year : 1900 + year;
                return new Date(year, month, day);
            }},
            { regex: /^(\d{4})[/.-](\d{1,2})[/.-]([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
                const year = parseInt(m[1], 10);
                const day = parseInt(m[2], 10);
                const month = monthMap[m[3].toLowerCase()];
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^(\d{4})[/.-]([A-Za-z]+)[/.-](\d{1,2})$/, handler: (m: RegExpMatchArray) => {
                const year = parseInt(m[1], 10);
                const month = monthMap[m[2].toLowerCase()];
                const day = parseInt(m[3], 10);
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = monthMap[m[2].toLowerCase()];
                const year = parseInt(m[3], 10);
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^([A-Za-z]{3,})\s+(\d{1,2})(?:\s+(\d{4}))?$/, handler: (m: RegExpMatchArray) => {
                const month = monthMap[m[1].toLowerCase()];
                const day = parseInt(m[2], 10);
                const year = m[3] ? parseInt(m[3], 10) : currentYear;
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = monthMap[m[2].toLowerCase()];
                return month !== undefined ? new Date(currentYear, month, day) : null;
            }},
            { regex: /^([A-Za-z]+)[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                const month = monthMap[m[1].toLowerCase()];
                const year = parseInt(m[2], 10);
                return month !== undefined ? new Date(year, month, 1) : null;
            }},
            { regex: /^([A-Za-z]+)[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                const month = monthMap[m[1].toLowerCase()];
                let year = parseInt(m[2], 10);
                year = year < 50 ? 2000 + year : 1900 + year;
                return month !== undefined ? new Date(year, month, 1) : null;
            }},
            { regex: /^(\d{1,2})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                const month = parseInt(m[1], 10) - 1;
                const year = parseInt(m[2], 10);
                return new Date(year, month, 1);
            }},
            { regex: /^(\d{1,2})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                const month = parseInt(m[1], 10) - 1;
                let year = parseInt(m[2], 10);
                year = year < 50 ? 2000 + year : 1900 + year;
                return new Date(year, month, 1);
            }},
            { regex: /^(\d{4})$/, handler: (m: RegExpMatchArray) => {
                const year = parseInt(m[1], 10);
                return new Date(year, 0, 1);
            }},
            { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = parseInt(m[2], 10) - 1;
                const year = parseInt(m[3], 10);
                return new Date(year, month, day);
            }},
            { regex: /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = parseInt(m[2], 10) - 1;
                let year = parseInt(m[3], 10);
                year = year < 50 ? 2000 + year : 1900 + year;
                return new Date(year, month, day);
            }},
            { regex: /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/, handler: (m: RegExpMatchArray) => {
                const year = parseInt(m[1], 10);
                const month = parseInt(m[2], 10) - 1;
                const day = parseInt(m[3], 10);
                return new Date(year, month, day);
            }},
            { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{4})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = monthMap[m[2].toLowerCase()];
                const year = parseInt(m[3], 10);
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^(\d{1,2})[/.-]([A-Za-z]{3,})[/.-](\d{2})$/, handler: (m: RegExpMatchArray) => {
                const day = parseInt(m[1], 10);
                const month = monthMap[m[2].toLowerCase()];
                let year = parseInt(m[3], 10);
                year = year < 50 ? 2000 + year : 1900 + year;
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/, handler: (m: RegExpMatchArray) => {
                const month = monthMap[m[1].toLowerCase()];
                const day = parseInt(m[2], 10);
                const year = parseInt(m[3], 10);
                return month !== undefined ? new Date(year, month, day) : null;
            }},
            { regex: /^([A-Za-z]+)$/, handler: (m: RegExpMatchArray) => {
                const month = monthMap[m[1].toLowerCase()];
                return month !== undefined ? new Date(currentYear, month, 1) : null;
            }},
        ];

        for (const pattern of patterns) {
            const m = s.match(pattern.regex);
            if (m) {
                const result = pattern.handler(m);
                if (result && !isNaN(result.getTime()) && result.getFullYear() >= 1900 && result.getFullYear() <= 2100) {
                    return result;
                }
            }
        }
        
        return null;
    };

    return (
        <Box position="relative">
        <TableContainer sx={{ maxHeight: 300, background: 'var(--color-container-background)' }}>
            {rows.length === 0 ? (
                <Box 
                    sx={{ 
                        color: 'var(--color-primary)',
                        padding: 4,
                        textAlign: 'center',
                        minHeight: 75,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Player
                        autoplay
                        loop
                        animationData={okAnimation}
                        style={{ height: 160, width: 160 }}
                    />
                    <Typography sx={{ fontWeight: 500, fontSize: 16, fontStyle: 'italic' }}>
                        {placeholderText ?? 'No data'}
                    </Typography>
                </Box>
            ) : (
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        { onCheck &&
                            <TableCell padding="checkbox"></TableCell>
                        }
                        <TableCell  sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell  sx={{ fontWeight: 600 }}>Amount</TableCell>
                        <TableCell  sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell  sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{canEditCategories || !onCheck ? 'Categories' : 'Suggested Categories'}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                {rows.map((row, index) => {
                    const absoluteIndex = startIndex + index;
                    return (
                        <TableRow key={row.id} sx={{ background: absoluteIndex % 2 === 0 ? 'var(--color-container-background)' : '#fff' }}>
                            { onCheck &&
                                <TableCell padding="checkbox">
                                    <Checkbox checked={row.checked} onChange={e => onCheck?.(row.id, e.target.checked)} size="small"/>
                                </TableCell>
                            }
                            <TableCell>{formatDate(parseDate(row.date) || new Date())}</TableCell>
                            <TableCell>{formatAmount?.(Number(row.amount))}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell sx={{ color: row.suggestedCategory === 'Uncategorized' ? '#d26d0eff' : 'inherit'}}>{canEditCategories && categories ? 
                                <TreeDropdown
                                    options={categories}
                                    value={row.suggestedCategory}
                                    onChange={(selectedId, selectedOption) => onChangeCategory?.(row.id, selectedId || '')}
                                    height={20}
                                    placeholder="Select Category"
                                /> : row.suggestedCategory}
                            </TableCell>
                        </TableRow>
                    );
                })}
                </TableBody>
            </Table>
            )}
        </TableContainer>
        { false && onCheckAll && !canEditCategories && 
            <Box 
                sx={{ 
                    position: 'absolute', 
                    bottom: -65, 
                    left: 8, 
                    background: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: 2,
                    display: rows.length === 0 ? 'none' : 'block'
                }}
            >
                <Checkbox checked={allChecked} onChange={e => onCheckAll?.(e.target.checked)} size="small"/>
                Approve All
            </Box>
        }
        </Box>
    );
};

export default MatchTable;
